import { dsEnums } from '@vectronic/openApi-admin';
import { utcMoment } from '@vectronic/util';
import { Moment } from 'moment/moment';
import { RepeatIntervalType } from './camera-rule-config';

export const calculateWeekdays = (startDate: string | Moment | Date) => {
  const startDay = utcMoment(startDate).day();
  const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  return weekdays.map((v, i) => {
    const index = (startDay + i + 6) % 7;
    return { label: weekdays[index], value: weekdays[index] };
  });
};
export const getOffset = (startTime: string): number => {
  if (!startTime) return 0;
  const startDate = utcMoment(startTime, true);
  return startDate.hours() * 60 + startDate.minutes();
};
export const convertStartTimeToStartWeekday = (startTime: string, cycleTime: number) => {
  if (!isWeekly(cycleTime)) {
    return undefined;
  }
  const weekdays = calculateWeekdays(startTime);
  const offset = getOffset(startTime);
  return weekdays[Math.floor(offset / 1440)]?.value;
};
export const isWeekly = (cycleTime: number) => cycleTime === 10080 * 60;

export const isDaily = (cycleTime: number) => cycleTime === 1440 * 60;

export const convertStartTimeToStartHour = (startTime: string, cycleTime: number) => {
  const offset = getOffset(startTime);
  if (isWeekly(cycleTime) || isDaily(cycleTime)) {
    return Math.floor((offset % 1440) / 60);
  }
  return Math.floor(offset / 60);
};

export const convertStartTimeToStartMinute = (startTime: string) => {
  const offset = getOffset(startTime);
  return offset % 60;
};

export const convertStartAndOnTimeToEndWeekday = (
  startTime: string,
  onTime: number,
  cycleTime: number,
) => {
  if (!isWeekly(cycleTime)) return undefined;
  const offset = getOffset(startTime);
  const weekdays = calculateWeekdays(startTime);
  if (offset + onTime / 60 === 10080) {
    return weekdays[0]?.value;
  } else if (offset + onTime / 60 === 1440) {
    return weekdays[0]?.value;
  } else {
    return weekdays[Math.min(Math.floor((offset + onTime / 60) / 1440), 6)]?.value;
  }
};

/**
 * convert start time, on time and cycle time to end hour
 * @param startTime
 * @param onTime
 * @param cycleTime
 */
export const convertStartAndOnTimeToEndHour = (
  startTime: string,
  onTime: number,
  cycleTime: number,
) => {
  if (!startTime || !onTime || !cycleTime) return 24;
  const offset = getOffset(startTime);
  if (isWeekly(cycleTime) || isDaily(cycleTime)) {
    if (offset + onTime / 60 === 10080) {
      return 0;
    } else if (offset + onTime / 60 === 1440) {
      return 24;
    } else {
      return Math.floor(((offset + onTime / 60) % 1440) / 60);
    }
  }
  return (offset + onTime / 60) / 60;
};

export const convertStartAndOnTimeToEndMinute = (startTime: string, onTime: number) => {
  if (!startTime || !onTime) return 0;
  const offset = getOffset(startTime);
  if (offset + onTime / 60 === 10080) {
    return 0;
  } else if (offset + onTime / 60 === 1440) {
    return 0;
  } else {
    return (offset + onTime / 60) % 60;
  }
};

export const convertStartAndOnTimeToStartHour = (startTime: string) => {
  const offset = getOffset(startTime);
  return Math.floor((offset % 1440) / 60);
};

export const convertStartAndOnTimeToStartMinute = (startTime: string) => {
  const offset = getOffset(startTime);
  return Math.floor((offset % 1440) % 60);
};

export const convertPeriodLengthToRepeatIntervalType = (cycleTime: number): RepeatIntervalType => {
  if (cycleTime === 60 * 24 * 60 || !cycleTime) {
    return 'daily';
  }
  if (cycleTime === 60 * 24 * 7 * 60) {
    return 'weekly';
  }
  return 'advanced';
};
export const convertSizeToPhotoCount = (
  size: number,
  cameraScheduleRuleType: dsEnums.CameraScheduleRuleType,
) => {
  if (cameraScheduleRuleType === 'PICTURE') {
    return size;
  }
  return undefined;
};
export const convertMainTriggerToGpsSkipCount = (
  mainTriggerValue: number,
  cameraScheduleRuleMainTrigger: dsEnums.CameraScheduleRuleMainTrigger,
) => {
  if (cameraScheduleRuleMainTrigger === 'GPS') {
    return mainTriggerValue || 0;
  }
  return undefined;
};
export const convertMainTriggerToVitExpected = (
  mainTriggerValue: number,
  cameraScheduleRuleMainTrigger: dsEnums.CameraScheduleRuleMainTrigger,
) => {
  if (cameraScheduleRuleMainTrigger === 'VIT') {
    return mainTriggerValue;
  }
  return undefined;
};
export const convertCycleTimeToRepeatIntervalDay = (cycleTime: number): number => {
  return Math.floor(cycleTime / (1440 * 60));
};

export const convertCycleTimeToRepeatIntervalHour = (cycleTime: number): number => {
  const minutes = (cycleTime / 60) % 60;
  const days = Math.floor(cycleTime / (1440 * 60));
  return (cycleTime / 60 - days * 1440 - minutes) / 60;
};

export const convertCycleTimeToRepeatIntervalMinute = (cycleTime: number): number => {
  return (cycleTime / 60) % 60;
};
export const convertMainTriggerToTimeIntervalDay = (mainTriggerValue: number): number => {
  return Math.floor(mainTriggerValue / 1440);
};

export const convertMainTriggerToTimeIntervalHour = (mainTriggerValue: number): number => {
  const minutes = mainTriggerValue % 60;
  const days = Math.floor(mainTriggerValue / 1440);
  return (mainTriggerValue - days * 1440 - minutes) / 60;
};

export const convertMainTriggerToTimeIntervalMinute = (mainTriggerValue: number): number => {
  return mainTriggerValue % 60;
};
export const convertMainTriggerToProximityActiveTimeDay = (mainTriggerValue: number): number => {
  return Math.floor(mainTriggerValue / 1440);
};

export const convertMainTriggerToProximityActiveTimeHour = convertMainTriggerToTimeIntervalHour;

export const convertMainTriggerToProximityActiveTimeMinute = (mainTriggerValue: number): number => {
  return mainTriggerValue % 60;
};

export const convertSizeToVideoLengthHours = (size: number): number => {
  return Math.floor(size / 3600);
};

export const convertSizeToVideoLengthMinutes = (size: number): number => {
  return Math.floor((size % 3600) / 60);
};

export const convertSizeToVideoLengthSeconds = (size: number): number => {
  return size % 60;
};
export const convertPictureIntervalToMinutes = (pictureInterval: number): number => {
  return Math.floor(pictureInterval / 60);
};

export const convertPictureIntervalToSeconds = (pictureInterval: number): number => {
  return pictureInterval % 60;
};
